import React, { useRef, useState } from "react";
import { Autocomplete, Box, Button, Chip, CircularProgress, IconButton, Menu, MenuItem, Stack, Tab, Tabs, TextField, Tooltip, Typography } from "@mui/material";
import { Bot, MessageSquareQuote, Paperclip, Send, Smile, X, Languages, Zap } from "lucide-react";
import { hd, errorMessage } from "./api";
import { useHd } from "./HelpdeskContext";
import { fileSize } from "./ui";
import RichReply, { textToHtml } from "./RichReply";
import { T, TYPE, primaryButtonSx } from "../user/complaints/shared";

const EMOJIS = ["😊", "🙏", "👍", "🎉", "✅", "😔", "🙂", "👋", "❤️", "🤝"];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Human summary of a macro's actions for the "will also…" chip. */
export const describeMacro = (ctx, actions = []) =>
  actions.map(({ type, params = {} }) => {
    if (type === "set_status") return `status → ${ctx.statusById.get(String(params.statusId))?.name || "?"}`;
    if (type === "set_priority") return `priority → ${ctx.priorityById.get(String(params.priorityId))?.name || "?"}`;
    if (type === "assign_agent") return `assign → ${params.assigneeId === "me" ? "me" : ctx.agentById.get(String(params.assigneeId))?.name || "?"}`;
    if (type === "assign_team") return `team → ${ctx.teamById.get(String(params.teamId))?.name || "?"}`;
    if (type === "add_tags") return `tags + ${(params.tags || []).join(", ")}`;
    return type;
  }).join(" · ");

/**
 * Reply / internal-note composer.
 *   emailMode: the reply goes out by email -> rich text + CC/BCC
 *   onSend({content, html, cc, bcc, attachmentIds, internal, statusId})
 *   onMacro(cannedId) runs a canned response's actions after a successful send
 */
export default function Composer({ ticketId, onSend, onMacro, allowNote = true, showStatus = true, onTyping, placeholder, disabled, aiEnabled, vars = {}, emailMode = false }) {
  const ctx = useHd();
  const [tab, setTab] = useState("reply");
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [statusId, setStatusId] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiInfo, setAiInfo] = useState(null);
  const [emojiEl, setEmojiEl] = useState(null);
  const [cannedEl, setCannedEl] = useState(null);
  const [langEl, setLangEl] = useState(null);
  const [html, setHtml] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [macro, setMacro] = useState(null);
  const fileInput = useRef(null);
  const typingTimer = useRef(null);
  const editorRef = useRef(null);
  const internal = tab === "note";
  // email replies use the rich editor; chat replies and internal notes stay plain text
  const rich = emailMode && !internal;
  const setDraft = (plain) => {
    setText(plain);
    setHtml(textToHtml(plain));
  };

  const change = (v) => {
    setText(v);
    if (onTyping && !internal) {
      onTyping(true);
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => onTyping(false), 2500);
    }
  };

  const pick = async (list) => {
    if (!list?.length) return;
    setUploading(true);
    try {
      const saved = await hd.upload(list, ticketId);
      setFiles((f) => [...f, ...saved]);
    } catch (err) {
      ctx.notify(errorMessage(err, "Upload failed"), "error");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const send = async () => {
    if (!text.trim() && !files.length) return;
    const badAddress = [...cc, ...bcc].find((e) => !EMAIL_RE.test(e));
    if (rich && badAddress) {
      ctx.notify(`"${badAddress}" is not a valid email address`, "error");
      return;
    }
    setSending(true);
    try {
      await onSend({
        content: text.trim(), html: rich && html ? html : undefined, cc: rich ? cc : undefined, bcc: rich ? bcc : undefined,
        attachmentIds: files.map((f) => f._id), internal, statusId: statusId || undefined,
      });
      const pendingMacro = macro;
      setText("");
      setHtml("");
      setFiles([]);
      setStatusId("");
      setAiInfo(null);
      setCc([]);
      setBcc([]);
      setShowCc(false);
      setMacro(null);
      onTyping?.(false);
      if (pendingMacro && onMacro && !internal) {
        try {
          await onMacro(pendingMacro._id);
        } catch (err) {
          ctx.notify(errorMessage(err, "The macro actions could not be applied"), "warning");
        }
      }
    } catch (err) {
      ctx.notify(errorMessage(err, "Could not send"), "error");
    } finally {
      setSending(false);
    }
  };

  const suggest = async (language) => {
    if (!ticketId) return;
    setAiBusy(true);
    try {
      const s = await hd.aiReply(ticketId, { language });
      setDraft(s.reply);
      setAiInfo(s);
      setTab("reply");
    } catch (err) {
      ctx.notify(errorMessage(err, "AI suggestion unavailable"), "warning");
    } finally {
      setAiBusy(false);
    }
  };

  const translate = async (target) => {
    setLangEl(null);
    if (!text.trim()) return;
    setAiBusy(true);
    try {
      const r = await hd.aiTranslate({ text, target });
      setDraft(r.translation);
    } catch (err) {
      ctx.notify(errorMessage(err, "Translation unavailable"), "warning");
    } finally {
      setAiBusy(false);
    }
  };

  const applyCanned = (c) => {
    setCannedEl(null);
    const filled = String(c.body).replace(/{{\s*([a-z_]+)\s*}}/g, (all, k) => (vars[k] != null ? String(vars[k]) : all));
    if (rich && editorRef.current) editorRef.current.chain().focus("end").insertContent(textToHtml(filled)).run();
    else setText((t) => (t ? `${t}\n${filled}` : filled));
    if (c.actions?.length && !internal) setMacro(c);
  };
  const addEmoji = (e) => {
    setEmojiEl(null);
    if (rich && editorRef.current) editorRef.current.chain().focus().insertContent(e).run();
    else setText((t) => t + e);
  };

  const canReply = ctx.can("tickets.reply") || ctx.can("chat.reply");
  const aiOn = aiEnabled ?? (ctx.settings?.ai?.enabled && ctx.settings?.ai?.copilotEnabled !== false && ctx.can("ai.use"));

  return (
    <Box sx={{ borderTop: `1px solid ${T.border}`, bgcolor: "#fff" }}>
      {allowNote && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 36, px: 1, "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontSize: TYPE.small, fontWeight: 600 } }}>
          <Tab value="reply" label="Reply to customer" disabled={!canReply} />
          {ctx.can("notes.internal") && <Tab value="note" label="Internal note" />}
        </Tabs>
      )}
      <Box sx={{ px: 2, pb: 1.5, pt: allowNote ? 0.5 : 1.5, bgcolor: internal ? "#FFFAEB" : "#fff" }}>
        {rich && (
          <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
            {showCc ? (
              <Stack spacing={1} sx={{ flexGrow: 1 }}>
                <RecipientField label="Cc" value={cc} onChange={setCc} disabled={disabled} />
                <RecipientField label="Bcc" value={bcc} onChange={setBcc} disabled={disabled} />
              </Stack>
            ) : <Box sx={{ flexGrow: 1 }} />}
            <Button size="small" onClick={() => setShowCc((v) => !v)} sx={{ textTransform: "none", minWidth: 0, fontWeight: 600 }}>{showCc ? "Hide Cc/Bcc" : "Cc/Bcc"}</Button>
          </Stack>
        )}
        {rich ? (
          <RichReply value={html} editorRef={editorRef} disabled={disabled} onSubmit={send} placeholder={placeholder || "Type your reply…"}
            onChange={({ html: h, text: t }) => { setHtml(h); change(t); }} />
        ) : (
          <TextField
            multiline minRows={2} maxRows={10} fullWidth value={text} disabled={disabled}
            placeholder={internal ? "Only your team can see this note…" : placeholder || "Type your reply…"}
            onChange={(e) => change(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) send();
            }}
            sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: "10px", fontSize: TYPE.body } }}
          />
        )}
        {macro && !internal && (
          <Chip size="small" icon={<Zap size={12} />} onDelete={() => setMacro(null)} deleteIcon={<X size={12} />} sx={{ mt: 1, bgcolor: "#F4EBFF", color: "#5925DC", maxWidth: "100%" }}
            label={`After sending, macro “${macro.title}” will also: ${describeMacro(ctx, macro.actions)}`} />
        )}
        {aiInfo && (
          <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary, mt: 0.5 }}>
            AI draft · confidence {Math.round((aiInfo.confidence || 0) * 100)}%{aiInfo.articles?.length ? ` · based on: ${aiInfo.articles.filter((a) => aiInfo.usedArticleIds.includes(String(a._id))).map((a) => a.title).join(", ") || "no article"}` : ""} — review before sending
          </Typography>
        )}
        {files.length > 0 && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {files.map((f) => <Chip key={f._id} size="small" label={`${f.fileName} · ${fileSize(f.size)}`} onDelete={() => setFiles((x) => x.filter((y) => y._id !== f._id))} deleteIcon={<X size={12} />} />)}
          </Stack>
        )}
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          <input ref={fileInput} type="file" multiple hidden onChange={(e) => pick(e.target.files)} />
          <Tooltip title="Attach files"><span><IconButton size="small" onClick={() => fileInput.current?.click()} disabled={uploading || disabled}>{uploading ? <CircularProgress size={16} /> : <Paperclip size={17} />}</IconButton></span></Tooltip>
          <Tooltip title="Emoji"><IconButton size="small" onClick={(e) => setEmojiEl(e.currentTarget)} disabled={disabled}><Smile size={17} /></IconButton></Tooltip>
          {ctx.cannedResponses.length > 0 && <Tooltip title="Canned responses"><IconButton size="small" onClick={(e) => setCannedEl(e.currentTarget)} disabled={disabled}><MessageSquareQuote size={17} /></IconButton></Tooltip>}
          {aiOn && ticketId && !internal && (
            <>
              <Button size="small" startIcon={aiBusy ? <CircularProgress size={14} /> : <Bot size={15} />} disabled={aiBusy || disabled} onClick={() => suggest()} sx={{ textTransform: "none", fontWeight: 600 }}>AI suggest reply</Button>
              <Tooltip title="Translate draft"><span><IconButton size="small" onClick={(e) => setLangEl(e.currentTarget)} disabled={aiBusy || !text.trim()}><Languages size={17} /></IconButton></span></Tooltip>
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {showStatus && !internal && (
            <TextField select size="small" label="Then set status" value={statusId} onChange={(e) => setStatusId(e.target.value)} sx={{ minWidth: 170 }}>
              <MenuItem value="">Keep current</MenuItem>
              {ctx.statuses.filter((s) => s.isActive && !["resolved", "closed"].includes(s.type)).map((s) => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
            </TextField>
          )}
          <Button variant="contained" sx={{ ...primaryButtonSx, ...(internal ? { bgcolor: "#B54708", "&:hover": { bgcolor: "#93370D" } } : {}) }} endIcon={sending ? <CircularProgress size={14} color="inherit" /> : <Send size={15} />} onClick={send} disabled={sending || disabled || (!text.trim() && !files.length)}>
            {internal ? "Add note" : "Send"}
          </Button>
        </Stack>
      </Box>
      <Menu anchorEl={emojiEl} open={Boolean(emojiEl)} onClose={() => setEmojiEl(null)}>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 36px)", p: 1 }}>
          {EMOJIS.map((e) => <IconButton key={e} size="small" onClick={() => addEmoji(e)}>{e}</IconButton>)}
        </Box>
      </Menu>
      <Menu anchorEl={cannedEl} open={Boolean(cannedEl)} onClose={() => setCannedEl(null)}>
        {ctx.cannedResponses.map((c) => (
          <MenuItem key={c._id} onClick={() => applyCanned(c)} sx={{ fontSize: TYPE.small, gap: 1 }}>
            {c.actions?.length > 0 && <Zap size={13} color="#7A5AF8" />}{c.title}{c.shortcut ? ` (${c.shortcut})` : ""}
          </MenuItem>
        ))}
      </Menu>
      <Menu anchorEl={langEl} open={Boolean(langEl)} onClose={() => setLangEl(null)}>
        {[["en", "English"], ["hi", "Hindi"], ["hinglish", "Hinglish"], ["ar", "Arabic"]].map(([k, l]) => <MenuItem key={k} onClick={() => translate(k)}>{l}</MenuItem>)}
      </Menu>
    </Box>
  );
}

/** Free-typed email chips: Enter, comma or blur adds an address. */
const RecipientField = ({ label, value, onChange, disabled }) => {
  const [input, setInput] = useState("");
  const commit = (raw) => {
    const parts = String(raw).split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
    if (parts.length) onChange([...new Set([...value, ...parts])].slice(0, 10));
    setInput("");
  };
  return (
    <Autocomplete multiple freeSolo options={[]} value={value} inputValue={input} disabled={disabled}
      onInputChange={(_, v, reason) => { if (reason === "input") { if (/[,;]\s*$/.test(v)) commit(v); else setInput(v); } }}
      onChange={(_, v) => onChange(v.map(String).slice(0, 10))}
      renderTags={(v, getTagProps) => v.map((o, i) => <Chip size="small" label={o} {...getTagProps({ index: i })} key={o} color={EMAIL_RE.test(o) ? "default" : "error"} variant={EMAIL_RE.test(o) ? "filled" : "outlined"} />)}
      renderInput={(params) => <TextField {...params} size="small" label={label} placeholder={value.length ? "" : "name@example.com"} onBlur={() => input && commit(input)} />}
    />
  );
};
